import './Map.scss'

import H from '@here/maps-api-for-javascript/bin/mapsjs.bundle.harp'
import classNames from 'classnames'
import { useFlag } from 'components/App/hooks/useFlag'
import DiscoveryPage from 'components/DiscoveryPage'
import LandingPage from 'components/LandingPage'
import { ActionParamPlaceHolder, UserPosition } from 'data/model'
import { RootState } from 'data/reducer'
import { setContextMenu } from 'data/reducer/map'
import { setPdcItem } from 'data/reducer/pdc'
import { setSelectedRoute } from 'data/reducer/routePlanner'
import {
  fetchBrowseItemList,
  fetchLocationAddress,
  fetchSearchItemList,
  setBrowseItemList,
} from 'data/reducer/search'
import { fetchCurrentPosition } from 'data/reducer/user'
import { useAppDispatch, useAppSelector } from 'data/store/hooks'
import { AppEvent, logAppEvent } from 'lib/analytics'
import { mapConfig } from 'lib/config'
import { PageType, useNavigation } from 'lib/hooks'
import { MapViewEvents } from 'map/events'
import {
  BaseLayerNames,
  createHindiLayers,
  createLayers,
  getMapLayerName,
  TrafficLayerName,
} from 'map/layers'
import { createMap } from 'map/map'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  NavigateFunction,
  Route,
  Routes,
  useLocation,
  useMatch,
  useNavigate,
  useSearchParams,
} from 'react-router-dom'
import { getMapPadding, isWithinMapBounds, isWithinZoomRange } from 'utils/map'
import { isDiffWithinTolerance, roundToPrecision } from 'utils/math'
import { createMapSearchParam, MapSearchParam, parseMapSearchParam } from 'utils/router'

import { Account } from '../Account'
import { handleError, handleToken } from '../Account/data/reducer'
import { ViewType } from '../Collections'
import { PdsData, ViewEvent } from '../Collections/Collections'
import { MapContextMenuWrapper } from '../MapContextMenu'
import RoutePlanner from '../RoutePlanner'
import CookiePrompt from './CookiePrompt'
import { MapLayers, MarkerEvent, SearchInAreaBounds } from './data/model'
import MapLayerSwitch from './LayerSwitch'
import MyLocationButton from './MyLocationButton'
import OrientationButton, { OrientationButtonMode } from './OrientationButton'
import PdcPage from './pages/PdcPage'
import PositionMarker from './PositionMarker'
import SearchInAreaButton from './SearchInAreaButton'
import ZoomControls from './ZoomControls'
import { ZoomAction } from './ZoomControls/ZoomControls'

export default function Map(): JSX.Element {
  const [searchParams] = useSearchParams()
  const matchDiscover = useMatch('/discover/:query')
  const { goTo } = useNavigation()
  const navigate = useNavigate()
  const { hash, search } = useLocation()

  const mapSearchParamStr = searchParams.get('map')

  const mapSearchParam = useMemo(
    () => (mapSearchParamStr !== null ? parseMapSearchParam(mapSearchParamStr) : undefined),
    [mapSearchParamStr]
  )

  const dispatch = useAppDispatch()

  const isSatelliteSaturationFeatureFlagEnabled = useFlag('satelliteSaturation')

  const appLanguage = useAppSelector((state: RootState) => state.app.language)
  const browserCenter = useAppSelector((state) => state.app.browserMapPosition)
  const contextMenu = useAppSelector((state) => state.map.contextMenu)
  const searchList = useAppSelector((state: RootState) => state.search.searchItemList)
  const pdcItem = useAppSelector((state: RootState) => state.pdc.pdcItem)
  const browseList = useAppSelector((state: RootState) => state.search.browseList)
  const userCountryCode = useAppSelector((state: RootState) => state.user.countryCode)
  const userPosition = useAppSelector((state: RootState) => state.user.currentPosition)
  const { oidcState } = useAppSelector((state: RootState) => state.account)

  const browserCenterRef = useRef(browserCenter)
  const isMarkersGroupRedrawRef = useRef<boolean>(false)
  const lastMarkersBoundsRef = useRef<SearchInAreaBounds>()
  const mapContainerRef = useRef<HTMLDivElement>(null)
  const mapLayers = useRef(createLayers(window.devicePixelRatio, appLanguage, userCountryCode))
  const mapSearchParamRef = useRef<MapSearchParam | undefined>(mapSearchParam)
  const markersGroupRef = useRef<H.map.Group>(new H.map.Group())
  const markersPositionRef = useRef<H.map.Group>(new H.map.Group())
  const matchDiscoverRef = useRef(matchDiscover)
  const navigateRef = useRef<NavigateFunction>(navigate)
  const mapLayersRef = useRef<MapLayers>({
    [BaseLayerNames.SATELLITE]: mapLayers.current.hybrid.day.raster,
    [BaseLayerNames.OMV]: mapLayers.current.vector.normal.map,
    [TrafficLayerName]: mapLayers.current.vector.traffic.map,
  })
  const hindiLayers = useRef(createHindiLayers())

  // TODO: Remove this once JSLA fixes the translation for hindi
  if (appLanguage === 'hi-IN') {
    mapLayersRef.current = {
      [BaseLayerNames.SATELLITE]: hindiLayers.current.satelliteLayer,
      [BaseLayerNames.OMV]: hindiLayers.current.omvLayer,
      [TrafficLayerName]: mapLayers.current.vector.traffic.map,
    }
  }

  const [isCookiePromptDisplayed, setIsCookiePromptDisplayed] = useState(false)
  const [isSearchInAreaButtonDisplayed, setIsSearchInAreaButtonDisplayed] = useState(false)
  const [map, setMap] = useState<H.Map>()
  const orientationRef = useRef<OrientationButtonMode>(OrientationButtonMode.Default)
  const [collectionView, setCollectionView] = useState<ViewType>(ViewType.collectionsCard)
  const [collectionMarkers, setCollectionMarkers] = useState<PdsData[]>([])
  const [orientation, setOrientation] = useState<OrientationButtonMode>(
    OrientationButtonMode.Default
  )

  // Keep the previous orientation value to be used inside 3rd party event handlers
  useEffect(() => {
    orientationRef.current = orientation
  }, [orientation])

  // Keeping "navigate" function in a ref, so other hooks don't depend on "navigate" change.
  // Navigate instance changes when the router path is changing
  useEffect(() => {
    navigateRef.current = navigate
  }, [navigate])

  // Keeping matchDiscover in a ref, so other hooks don't depend to it, but get the ref value
  // when that is needed(i.e search in area button)
  useEffect(() => {
    matchDiscoverRef.current = matchDiscover
  }, [matchDiscover])

  useEffect(() => {
    if (browseList?.length > 0) {
      const item = browseList[0]
      const isRoutePlannerOpen = location.pathname.startsWith('/directions')

      if (isRoutePlannerOpen) {
        dispatch(setSelectedRoute(undefined))
        dispatch(
          setPdcItem({
            id: item.id,
            position: item.position,
            title: item.title,
          })
        )
      } else {
        goTo({
          type: PageType.PDC,
          url: `/p/${encodeURIComponent(item.id)}`,
        })
      }
      dispatch(setBrowseItemList([]))
    }
  }, [browseList, dispatch, goTo])

  useEffect(() => {
    const consent = localStorage.getItem('CONSENT')

    setIsCookiePromptDisplayed(consent === null)
  }, [setIsCookiePromptDisplayed])

  // TODO temp fix for release
  const handleConsentRejection = (): void => {
    localStorage.setItem('CONSENT', 'REJECTED')
    setIsCookiePromptDisplayed(false)
  }

  const handleConsentAcceptance = (): void => {
    localStorage.setItem('CONSENT', 'ACCEPTED')
    setIsCookiePromptDisplayed(false)
  }

  const handleWaypointMarkerCreation = useCallback((marker: H.map.Marker): void => {
    markersGroupRef.current.addObject(marker)
  }, [])

  const handlePositionMarkerCreation = useCallback((marker: H.map.Marker): void => {
    markersPositionRef.current.addObject(marker)
    markersPositionRef.current.dispatchEvent(new H.util.Event(MarkerEvent.MarkersUpdate))
  }, [])

  const handleSearchInAreaClick = useCallback(() => {
    if (map) {
      // TODO this part of bounding box extraction can be set inside state management,
      // and extracted as information from epics or components that are need it.
      const bbox = map.getViewModel().getLookAtData().bounds?.getBoundingBox() as H.geo.Rect
      const { lat: latNorth, lng: lngWest } = bbox.getTopLeft()
      const { lat: latSouth, lng: lngEast } = bbox.getBottomRight()
      const bboxStr = `bbox:${lngWest},${latSouth},${lngEast},${latNorth}`
      dispatch(fetchSearchItemList(ActionParamPlaceHolder.FromState, undefined, bboxStr))
    }
  }, [dispatch, map])

  const handleMapOrientation = (buttonOrientationMode: OrientationButtonMode): void => {
    switch (buttonOrientationMode) {
      case OrientationButtonMode.Default:
        map && map.getViewModel().setLookAtData({ tilt: 73 }, mapConfig.zoomAnimation)
        break
      case OrientationButtonMode.TwoDimension:
        map && map.getViewModel().setLookAtData({ tilt: 0 }, mapConfig.zoomAnimation)
        break
      case OrientationButtonMode.Compass:
      default:
        map && map.getViewModel().setLookAtData({ heading: 180 }, mapConfig.zoomAnimation)
        break
    }
  }

  useEffect(() => {
    // Handling the edge case where markers aren't redrawn but the "search in area" button is displayed
    const position = map?.getCenter()
    if (map && position) {
      lastMarkersBoundsRef.current = {
        lat: position?.lat,
        lng: position?.lng,
        zoom: map?.getZoom(),
      }
      setIsSearchInAreaButtonDisplayed(false)
    }
  }, [map, searchList])

  // Register a custom event that triggers when all the markers are rendered
  // Event updates map's bounding box to marker's limits.
  useEffect(() => {
    const changeBoundingBox = (): void => {
      markersGroupRef.current.getBoundingBox()?.getCenter()
      isMarkersGroupRedrawRef.current = true
      const bbox = markersGroupRef.current.getBoundingBox()

      if (markersGroupRef.current.getObjects().length > 1) {
        map?.getViewModel().setLookAtData(
          {
            bounds: bbox,
          },
          mapConfig.zoomAnimation
        )
      } else {
        if (pdcItem?.mapView !== undefined) {
          const bounds = new H.geo.Rect(
            pdcItem.mapView.north,
            pdcItem.mapView.west,
            pdcItem.mapView.south,
            pdcItem.mapView.east
          )

          map?.getViewModel().setLookAtData({ bounds }, mapConfig.zoomAnimation)
        } else {
          map?.getViewModel().setLookAtData(
            {
              position: markersGroupRef.current.getBoundingBox()?.getCenter(),
              zoom: mapConfig.geolocation.defaultZoomLevelPosition,
            },
            mapConfig.zoomAnimation
          )
        }
      }
    }

    const markers = markersGroupRef.current
    markers.addEventListener(MarkerEvent.MarkersUpdate, changeBoundingBox)

    return () => {
      markers.removeEventListener(MarkerEvent.MarkersUpdate, changeBoundingBox)
    }
  }, [map, pdcItem])

  const switchBaseLayer = useCallback(
    (layerName: BaseLayerNames) => {
      if (map === undefined) {
        return
      }

      const mapLayerName = getMapLayerName(layerName)
      map.setBaseLayer(mapLayersRef.current[mapLayerName] as H.map.layer.TileLayer)

      // TODO: Remove this once JSLA fixes the translation for hindi
      if (appLanguage !== 'hi-IN') {
        if (layerName === BaseLayerNames.SATELLITE) {
          map.addLayer(mapLayers.current.hybrid.day.vector)
        } else {
          map.removeLayer(mapLayers.current.hybrid.day.vector)
        }
      }

      if (mapSearchParamStr !== null) {
        const mapSearchParam = createMapSearchParam({ baseLayer: layerName }, mapSearchParamStr)
        if (mapSearchParam !== undefined) {
          navigateRef.current({ search: `?map=${mapSearchParam}` }, { replace: true })
        }
      } else {
        const { lat, lng } = map.getCenter()
        const zoom = map.getZoom()
        const { paramPrecision } = mapConfig.url
        const nextMapSearchParam = createMapSearchParam({
          baseLayer: layerName,
          lat: roundToPrecision(lat, paramPrecision.coord).toString(),
          lng: roundToPrecision(lng, paramPrecision.coord).toString(),
          zoom: roundToPrecision(zoom, paramPrecision.zoom).toString(),
        })
        navigateRef.current({ search: `?map=${nextMapSearchParam as string}` }, { replace: true })
      }
    },
    [map, mapSearchParamStr, appLanguage]
  )

  // toggle traffic layer
  useEffect(() => {
    const layerName =
      mapSearchParam !== undefined
        ? (mapSearchParam.baseLayer as BaseLayerNames)
        : BaseLayerNames.OMV
    const isEnabled =
      layerName === BaseLayerNames.TRAFFIC || layerName === BaseLayerNames.SATELLITE_TRAFFIC
    const currentTrafficLayer = mapLayersRef.current[TrafficLayerName]
    const mapLayerName = getMapLayerName(layerName)

    if (isEnabled && map !== undefined) {
      const newTrafficLayer =
        mapLayerName === BaseLayerNames.OMV
          ? mapLayers.current.vector.traffic.map
          : mapLayers.current.hybrid.day.traffic
      if (!currentTrafficLayer) {
        map?.addLayer(newTrafficLayer)
      } else if (currentTrafficLayer !== newTrafficLayer) {
        map?.removeLayer(mapLayersRef.current[TrafficLayerName] as H.map.layer.TileLayer)
        map?.addLayer(newTrafficLayer)
      }

      mapLayersRef.current[TrafficLayerName] = newTrafficLayer
    } else if (currentTrafficLayer) {
      map?.removeLayer(currentTrafficLayer)
      delete mapLayersRef.current[TrafficLayerName]
    }
  }, [map, mapSearchParam])

  const onContextMenu = useCallback(
    (map: H.Map, event: H.mapevents.ContextMenuEvent): void => {
      const point = map.screenToGeo(event.viewportX, event.viewportY)

      if (point !== null) {
        dispatch(
          fetchLocationAddress(
            {
              lat: point.lat,
              lng: point.lng,
            },
            'address'
          )
        )

        dispatch(setContextMenu({ position: { lat: point.lat, lng: point.lng } }))
      }
    },
    [dispatch]
  )

  interface PoiTapData {
    properties: {
      country: string
      kind: string
      name: string
      pds_category: string
    }
  }

  const onPoiTap = useCallback(
    (map: H.Map, evt: H.mapevents.Event) => {
      if (evt.target !== null) {
        const data = (evt.target as H.map.Object).getData() as PoiTapData

        const point = map.screenToGeo(evt.currentPointer.viewportX, evt.currentPointer.viewportY)
        if (
          point !== null &&
          data.properties?.pds_category?.length > 0 &&
          (evt.originalEvent as PointerEvent).button === 0
        ) {
          dispatch(
            fetchBrowseItemList(
              {
                lat: point.lat,
                lng: point.lng,
              },
              [data.properties.pds_category],
              2,
              data.properties.name
            )
          )
        }
      }
    },
    [dispatch]
  )

  // This effect instantiates a map on mount with options coming either from URL or config defaults.
  useEffect(() => {
    const layerName = mapSearchParamRef.current
      ? (mapSearchParamRef.current.baseLayer as BaseLayerNames)
      : undefined

    const mapLayerName = layerName ? getMapLayerName(layerName) : undefined

    const baseLayer = mapLayerName
      ? (mapLayersRef.current[mapLayerName] as H.map.layer.TileLayer)
      : appLanguage === 'hi-IN'
      ? hindiLayers.current.omvLayer
      : mapLayers.current.vector.normal.map

    const center =
      mapSearchParamRef.current === undefined
        ? { lat: browserCenterRef.current.lat, lng: browserCenterRef.current.lng }
        : {
            lat: mapSearchParamRef.current.lat,
            lng: mapSearchParamRef.current.lng,
          }
    const zoom =
      mapSearchParamRef.current === undefined
        ? mapConfig.geolocation.defaultZoomLevel
        : mapSearchParamRef.current.zoom

    // Adding this "magic" number since we'd like to see a bit of space around the outmost objects
    const { top, bottom, left, right } = getMapPadding()
    const padding = { bottom, left, right, top }

    const mapInstance = createMap(
      mapContainerRef.current as HTMLDivElement,
      baseLayer,
      {
        center,
        padding,
        zoom,
      },
      onContextMenu,
      onPoiTap
    )

    // Set the initial position on mount as searchInArea starting position
    lastMarkersBoundsRef.current = { lat: center.lat, lng: center.lng, zoom }

    if (appLanguage !== 'hi-IN') {
      if (mapSearchParamRef.current?.baseLayer === BaseLayerNames.SATELLITE) {
        mapInstance.addLayer(mapLayers.current.hybrid.day.vector)
      }
    }

    setMap(mapInstance)
    void logAppEvent(AppEvent.MapLoaded)

    const handleWindowResize = (): void => {
      const { top, bottom, left, right } = getMapPadding()
      mapInstance.getViewPort().setPadding(top, right, bottom, left)
      mapInstance.getViewPort().resize()
    }

    window.addEventListener('resize', handleWindowResize)

    mapInstance.addObject(markersGroupRef.current)
    mapInstance.addObject(markersPositionRef.current)

    dispatch(fetchCurrentPosition())

    return () => {
      mapInstance.dispose()
      window.removeEventListener('resize', handleWindowResize)
    }
  }, [dispatch, appLanguage, onContextMenu, onPoiTap])

  // This effect updates a ref, so it could be used in the "mapviewchangeend" subscription
  // without re-triggering its entire wrapping effect on "mapSearchParam" change.
  // It also repositions the map (if coord/zoom diff is larger than configured tolerance)
  // on "mapSearchParam" change, so back/forward button clicks (and other history changes) are handled.
  useEffect(() => {
    if (map === undefined || mapSearchParam === undefined) {
      return
    }

    mapSearchParamRef.current = mapSearchParam

    const { paramTolerance } = mapConfig.url
    const { lat, lng } = map.getCenter()
    const zoom = map.getZoom()

    // this comparison avoids triggering another "mapviewchangeend" event when
    // "mapSearchParam" was updated as a result of such event (see an effect below)
    if (
      isDiffWithinTolerance(lat, mapSearchParam.lat, paramTolerance.coord) &&
      isDiffWithinTolerance(lng, mapSearchParam.lng, paramTolerance.coord) &&
      isDiffWithinTolerance(zoom, mapSearchParam.zoom, paramTolerance.zoom)
    ) {
      return
    }

    // we set this true because it is just for repositioning fixation, and alignment
    // with params and user's interaction with the map
    map.getViewModel().setLookAtData({
      position: {
        lat: mapSearchParam.lat,
        lng: mapSearchParam.lng,
      },
      zoom: mapSearchParam.zoom,
    })
  }, [map, mapSearchParam])

  // This effect updates URL on map's "mapviewchangeend" event when coords or zoom diff is sufficient.
  useEffect(() => {
    if (map === undefined) {
      return
    }

    const handleMapViewChangeEnd = (): void => {
      const { paramPrecision, paramTolerance } = mapConfig.url
      const { lat, lng } = map.getCenter()
      const zoom = map.getZoom()
      const mapSearchParam = mapSearchParamRef.current

      if (
        mapSearchParam !== undefined &&
        isDiffWithinTolerance(lat, mapSearchParam.lat, paramTolerance.coord) &&
        isDiffWithinTolerance(lng, mapSearchParam.lng, paramTolerance.coord) &&
        isDiffWithinTolerance(zoom, mapSearchParam.zoom, paramTolerance.zoom)
      ) {
        return
      }

      // Update the last markerGroup bounds
      if (isMarkersGroupRedrawRef.current) {
        const position = map.getCenter()
        if (position !== undefined) {
          lastMarkersBoundsRef.current = {
            lat: position.lat,
            lng: position.lng,
            zoom: map.getZoom(),
          }
        }
      }

      // Render searchInArea button when user is panning the map,
      // or mark the view change action if originates by user input(markers not redrawn)
      const isOutOfBounds =
        mapSearchParam !== undefined &&
        lastMarkersBoundsRef.current !== undefined &&
        !(
          isWithinMapBounds(
            {
              lat: lastMarkersBoundsRef.current.lat,
              lng: lastMarkersBoundsRef.current.lng,
            },
            map
          ) && isWithinZoomRange(lastMarkersBoundsRef.current.zoom, 2, map)
        )

      // Checking if the bounds have changed only in case url is /discover/:query
      // TODO we probably need to break down the Map component into Map page components in the future
      if (isOutOfBounds && matchDiscoverRef.current) {
        setIsSearchInAreaButtonDisplayed(!isMarkersGroupRedrawRef.current)
      }

      // mark the next action as a non marker update
      isMarkersGroupRedrawRef.current = false

      const nextMapSearchParam = createMapSearchParam({
        baseLayer:
          mapSearchParam?.baseLayer !== undefined ? mapSearchParam?.baseLayer : BaseLayerNames.OMV,
        lat: roundToPrecision(lat, paramPrecision.coord).toString(),
        lng: roundToPrecision(lng, paramPrecision.coord).toString(),
        zoom: roundToPrecision(zoom, paramPrecision.zoom).toString(),
      })

      if (nextMapSearchParam !== undefined) {
        console.debug('navigate() from handleMapViewChangeEnd()', nextMapSearchParam)
        navigateRef.current({ search: `?map=${nextMapSearchParam}` }, { replace: true })
      }
    }

    map.addEventListener(MapViewEvents.changeEnd, handleMapViewChangeEnd)

    return () => {
      map.removeEventListener(MapViewEvents.changeEnd, handleMapViewChangeEnd)
    }
  }, [map])

  // This effect register "mapviewchange" event and updates the orientation state of the respective button.
  useEffect(() => {
    if (map === undefined) {
      return
    }

    const handleMapViewChange = (): void => {
      // Updating the button orientation type to "compass" when user manually intervenes
      let { tilt, heading } = map.getViewModel().getLookAtData()

      tilt = roundToPrecision(tilt !== undefined ? tilt : 0, 1)
      heading = roundToPrecision(heading !== undefined ? heading : 0, 1)
      const hasManualOrientation = heading !== undefined && heading !== 180
      const is3D = tilt !== undefined && tilt > 0

      if (hasManualOrientation) {
        setOrientation(OrientationButtonMode.Compass)
      } else if (is3D) {
        setOrientation(OrientationButtonMode.TwoDimension)
      } else {
        setOrientation(OrientationButtonMode.Default)
      }
    }

    map.addEventListener(MapViewEvents.change, handleMapViewChange)

    return () => {
      map.removeEventListener(MapViewEvents.change, handleMapViewChange)
    }
  }, [map])

  // It's important to keep a single instance of "<Search />" always mounted,
  // because it manages user input, search results (with markers) and PDC.
  // As only an element matching a route can access "useParams()",
  // "<SearchUrlParamHandler />" reports back to "<Search />" via its render callback argument.

  const handleTrafficChange = useCallback(
    (isEnabled: boolean): void => {
      const layerName =
        mapSearchParamRef.current !== undefined
          ? (mapSearchParamRef.current.baseLayer as BaseLayerNames)
          : BaseLayerNames.OMV
      const nextMapSearchParam = createMapSearchParam(
        {
          baseLayer: isEnabled
            ? layerName === BaseLayerNames.OMV || layerName === BaseLayerNames.NORMAL
              ? BaseLayerNames.TRAFFIC
              : BaseLayerNames.SATELLITE_TRAFFIC
            : getMapLayerName(layerName),
        },
        mapSearchParamStr !== null ? mapSearchParamStr : undefined
      )
      navigateRef.current({ search: `?map=${nextMapSearchParam as string}` }, { replace: true })
    },
    [mapSearchParamStr]
  )

  const handleBaseLayerChange = useCallback(
    (layerName: BaseLayerNames): void => {
      switchBaseLayer(layerName)
    },
    [switchBaseLayer]
  )

  const handleZoomChange = useCallback(
    (zoomAction: ZoomAction): void => {
      if (zoomAction === ZoomAction.zoomIn && map) {
        map.setZoom(map.getZoom() + 1, true)
      } else if (zoomAction === ZoomAction.zoomOut && map) {
        map.setZoom(map.getZoom() - 1, true)
      }
    },
    [map]
  )

  const handleLookAtPosition = useCallback(
    (userPosition: UserPosition): void => {
      if (map && userPosition.coordinates) {
        const location: H.map.ViewModel.ILookAtData = {
          position: { lat: userPosition.coordinates.lat, lng: userPosition.coordinates.lng },
          zoom: mapConfig.geolocation.defaultZoomLevelPosition,
        }
        map.getViewModel().setLookAtData(location, mapConfig.zoomAnimation)
      }
    },
    [map]
  )

  const isTrafficEnabled =
    mapSearchParam?.baseLayer === BaseLayerNames.TRAFFIC ||
    mapSearchParam?.baseLayer === BaseLayerNames.SATELLITE_TRAFFIC

  const baseLayerName =
    mapSearchParam?.baseLayer !== undefined ? mapSearchParam?.baseLayer : BaseLayerNames.OMV

  const handleCollectionsView = useCallback((view: ViewEvent): void => {
    setCollectionView(view.type)
    if (view.type === ViewType.placesList) {
      setCollectionMarkers(view.payload as PdsData[])
    } else {
      setCollectionMarkers([])
    }
  }, [])

  useEffect(() => {
    if (hash) {
      dispatch(handleToken(hash))
    } else if (search) {
      dispatch(handleError(search))
    }
  }, [dispatch, search, hash])

  useEffect(() => {
    if (oidcState.pathName) {
      navigateRef.current(oidcState.pathName + oidcState.search + oidcState.hash, { replace: true })
    }
  }, [oidcState])

  return (
    <React.Fragment>
      <div
        className={classNames('Map__container', {
          'Map__container--satellite':
            (baseLayerName === BaseLayerNames.SATELLITE ||
              baseLayerName === BaseLayerNames.SATELLITE_TRAFFIC) &&
            isSatelliteSaturationFeatureFlagEnabled,
        })}
        ref={mapContainerRef}
      />
      {Boolean(contextMenu.position) && map && <MapContextMenuWrapper />}
      {map && (
        <React.Fragment>
          <div className="Map__panel Map__panel--right Map__panel--top">
            <Account />
          </div>
          {isCookiePromptDisplayed && (
            <div className="Map__notification">
              <CookiePrompt
                clickAccepted={handleConsentAcceptance}
                clickRejected={handleConsentRejection}
              />
            </div>
          )}
          <div className="Map__panel Map__panel--right Map__panel--bottom Map__panel--horizontal">
            <MapLayerSwitch
              baseLayerName={baseLayerName as BaseLayerNames}
              isTrafficEnabled={isTrafficEnabled}
              onBaseLayerChange={handleBaseLayerChange}
              onTrafficChange={handleTrafficChange}
            />
            <div className="Map__controls">
              <OrientationButton
                className="Map_orientation"
                mode={orientation}
                onClick={handleMapOrientation}
              />
              <MyLocationButton handleLookAtPosition={handleLookAtPosition} />
              <ZoomControls handleZoomChange={handleZoomChange} />
            </div>
          </div>
          {userPosition.coordinates && (
            <PositionMarker
              lat={userPosition.coordinates.lat}
              lng={userPosition.coordinates.lng}
              onMarkerCreate={handlePositionMarkerCreation}
            />
          )}
          <Routes>
            <Route
              path={`${mapConfig.url.path.discover}/:query`}
              element={
                <>
                  <DiscoveryPage markersGroup={markersGroupRef.current} />
                  {isSearchInAreaButtonDisplayed && (
                    <div className="Map__panel Map__panel--top Map__panel--center">
                      <SearchInAreaButton clickHandler={handleSearchInAreaClick} />
                    </div>
                  )}
                </>
              }
            />
            <Route
              path={`${mapConfig.url.path.pdc}/*`}
              element={<PdcPage markersGroup={markersGroupRef.current} />}
            />
            <Route
              path={`${mapConfig.url.path.location}`}
              element={<PdcPage markersGroup={markersGroupRef.current} />}
            />
            <Route
              path={`${mapConfig.url.path.directions}/*`}
              element={
                <RoutePlanner
                  map={map} // TODO: Remove once we create the map state
                  handleWaypointMarkerCreation={handleWaypointMarkerCreation}
                />
              }
            />
            <Route
              path="*"
              element={
                <LandingPage
                  markersGroup={markersGroupRef.current}
                  collectionMarkers={collectionMarkers}
                  handleCollectionsView={handleCollectionsView}
                  collectionView={collectionView}
                />
              }
            />
          </Routes>
        </React.Fragment>
      )}
    </React.Fragment>
  )
}
